import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { QueryConstraint } from "firebase/firestore";
import { Patient } from "risa-data-model";
import { FirestoreService } from "../../api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../api/firebase/references";
import { AppDispatch } from "../store/store";

export interface PatientDataWithId extends Patient {
  id: string;
}
export interface AllPatients {
  data: PatientDataWithId[] | null;
  count: number | null;
  loading: boolean;
  countLoading: boolean;
  error: string | null;
}
const initialState: AllPatients = {
  data: null,
  count: null,
  loading: false,
  countLoading: false,
  error: null,
};

export const patientsSlice = createSlice({
  name: "patients",
  initialState,
  reducers: {
    setPatients(state, action) {
      state.data = action.payload;
      state.error = null;
      state.loading = false;
    },
    setLoading(state, action) {
      state.loading = action.payload;
    },
    setError(state, action) {
      state.error = action.payload;
    },
    appendPatients(state, action: PayloadAction<PatientDataWithId[]>) {
      state.data = state.data
        ? [...state.data, ...action.payload]
        : action.payload;
    },
    resetPatients(state) {
      state.data = null;
      state.error = null;
      state.loading = false;
    },
    startFetchingCountsOfPatients(state) {
      state.countLoading = true;
      state.error = null;
      state.count = 0;
    },
    addCountsOfPatients(state, action: PayloadAction<number>) {
      state.count = action.payload;
      state.countLoading = false;
    },
    fetchSearchedPatients(state, action: PayloadAction<PatientDataWithId[]>) {
      state.data = action.payload;
      state.loading = false;
    },
  },
});
export const {
  setPatients,
  setLoading,
  setError,
  resetPatients,
  appendPatients,
  startFetchingCountsOfPatients,
  addCountsOfPatients,
  fetchSearchedPatients,
} = patientsSlice.actions;

export const getAllPatientsForHealthFacility =
  (healthFacilityId: string) => async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));
    const unsubscribe = FirestoreService.listenToAllDocuments<Patient>(
      FirestoreCollectionReference.patients(healthFacilityId),
      (patients) => {
        dispatch(setPatients(patients));
      },
      (error) => {
        dispatch(setError(error.message));
      },
    );

    return unsubscribe;
  };

export const getPatientsForHealthFacilityUsingBatching =
  (
    healthFacilityId: string,
    limitingValue?: number,
    ...queryConstraints: QueryConstraint[]
  ) =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));
    const unsubscribe =
      FirestoreService.listenToQueryCollectionForFirstBatch<PatientDataWithId>(
        FirestoreCollectionReference.patients(healthFacilityId),
        limitingValue || 10,
        (patients) => {
          dispatch(setPatients(patients));
        },
        (error) => {
          dispatch(setError(error.message));
        },
        ...queryConstraints,
      );

    return unsubscribe;
  };

export const fetchPatientCountsFromFirebase =
  (healthFacilityId: string, ...queryConstraints: QueryConstraint[]) =>
  async (dispatch: AppDispatch) => {
    dispatch(startFetchingCountsOfPatients());
    const unsubscribe = FirestoreService.listenToQueryCollection<Patient>(
      FirestoreCollectionReference.patients(healthFacilityId),
      (patients) => {
        dispatch(addCountsOfPatients(patients.length));
      },
      (error) => {
        dispatch(setError(error.message));
      },
      ...queryConstraints,
    );
    return unsubscribe;
  };

export const getPatientSearchResultFromFirebase =
  (healthFacilityId: string, ...queryConstraints: QueryConstraint[]) =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));
    const unsubscribe =
      FirestoreService.listenToQueryCollection<PatientDataWithId>(
        FirestoreCollectionReference.patients(healthFacilityId),
        (patients) => {
          dispatch(fetchSearchedPatients(patients));
        },
        (error) => {
          dispatch(setError(error.message));
        },
        ...queryConstraints,
      );
    return unsubscribe;
  };
export default patientsSlice.reducer;
